import React, { useEffect, useRef } from "react";
import { copySourceToTranslationShortcut } from "./TranslationViewSegmentRow";
import { cleanFilters } from "./segmentFilters";

type Props = {
  index: number;
  onChangeSegment: (index: number, value: string) => void;
  onFocusSegment: (index: number) => void;
  onCtrlL: (index: number) => void;
  onCtrlEnter: (index: number) => void;
  autoFocus: boolean;
  showLockIcons: boolean;
  displayTargetText: string;
  rawSourceText: string;
  isLocked: boolean;
};
export const _TranslationViewSegmentRowTextarea: React.FC<Props> = ({
  index,
  onChangeSegment,
  onFocusSegment,
  onCtrlL,
  onCtrlEnter,
  autoFocus,
  showLockIcons,
  displayTargetText,
  rawSourceText,
  isLocked,
}) => {
  const startContainer = useRef<Node>(null);
  const startOffset = useRef<number>(null);
  const startContainerNodeIndex = useRef<number>(null);
  const contentEditableRef = useRef<HTMLDivElement>(null);

  const findNextTextNode = (
    element: HTMLDivElement,
    index: number
  ): Node | null => {
    const nodes = element.childNodes;
    if (index + 1 > nodes.length) return null;
    const node = nodes[index];
    if (node.nodeType === node.TEXT_NODE) {
      return node;
    }
    return findNextTextNode(element, index + 1);
  };
  useEffect(() => {
    if (
      !contentEditableRef.current ||
      document.activeElement !== contentEditableRef.current
    )
      return;
    const element = contentEditableRef.current;
    const selection = window.getSelection();
    const range = document.createRange();
    if (startContainer.current && !isNaN(startOffset.current)) {
      let node: Node = null;
      element.childNodes.forEach((childNode) => {
        if (childNode.isEqualNode(startContainer.current)) {
          node = childNode;
        }
      });
      if (node) {
        // Select within a text node
        range.setStart(node, startOffset.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
        return;
      } else {
        // Select next Text node
        node = findNextTextNode(element, startContainerNodeIndex.current + 1);
        if (node) {
          range.setStart(node, 0);
          selection?.removeAllRanges();
          selection?.addRange(range);
          return;
        }
      }
    }
    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [displayTargetText]);

  if (isLocked && !showLockIcons) {
    return (
      <span
        data-testid={`translation-segment-locked-text-${index}`}
        className="tw-text-gray-500 tw-whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: displayTargetText }}
      ></span>
    );
  }
  return (
    <div
      ref={contentEditableRef}
      contentEditable
      dangerouslySetInnerHTML={{ __html: displayTargetText }}
      className="txu-textarea skip-default-styling txu-field-sizing-content tw-w-full !tw-p-1 !tw-text-sm tw-border-gray-300 tw-border-2 tw-rounded-md tw-whitespace-pre-wrap tw-min-h-8"
      onFocus={() => {
        onFocusSegment(index);
      }}
      autoFocus={autoFocus}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "l" && e.ctrlKey) {
          e.preventDefault();
          onCtrlL(index);
        }
        if (e.key === "Enter" && e.ctrlKey) {
          e.preventDefault();
          onCtrlEnter(index);
        }
        if (copySourceToTranslationShortcut(e)) {
          e.preventDefault();
          onChangeSegment(index, rawSourceText);
        }
      }}
      onBlur={() => {
        onFocusSegment(index);
      }}
      onInput={(e) => {
        try {
          const range = document.getSelection().getRangeAt(0);
          e.currentTarget.childNodes.forEach((node, index) => {
            if (node.isEqualNode(range.startContainer)) {
              startContainerNodeIndex.current = index;
            }
          });

          startContainer.current = range.startContainer;
          startOffset.current = range.startOffset;
        } catch {
          startContainer.current = null;
          startOffset.current = null;
        }
        onChangeSegment(index, cleanFilters(e.currentTarget.innerText));
      }}
      data-testid={`translation-segment-target-${index}`}
      data-translation-textarea={index} // sync with utils.ts.TRANSLATION_TEXTAREA_DATA_ATTRIBUTE constant
    />
  );
};

export const TranslationViewSegmentRowTextarea = React.memo(
  _TranslationViewSegmentRowTextarea,
  (prevProps, nextProps) => {
    return (
      prevProps.displayTargetText == nextProps.displayTargetText &&
      prevProps.isLocked == nextProps.isLocked
    );
  }
);
